import * as React from 'react'
import { Bell } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface TopbarBellProps {
  notifications: number
  notificationsPanel?: React.ReactNode
  title: string
}

/** Bell + badge + optional popover. Extracted from topbar.tsx so the host
 *  component stays under the 300-LOC guard ceiling. */
export function TopbarBell({ notifications, notificationsPanel, title }: TopbarBellProps) {
  const [open, setOpen] = React.useState(false)
  const buttonClass =
    'h-10 w-10 p-0 relative overflow-visible shrink-0 rounded-full flex items-center justify-center'
  const bell = (
    <Button
      variant="ghost"
      size="sm"
      className={buttonClass}
      title={title}
      aria-label={title}
      type="button"
    >
      <Bell className="h-6 w-6" strokeWidth={2} />
      {notifications > 0 && (
        <Badge
          variant="danger"
          className="absolute top-0 right-0 h-5 min-w-5 flex items-center justify-center px-1 text-xs rounded-full translate-x-1/2 -translate-y-1/2"
        >
          {notifications > 99 ? '99+' : notifications}
        </Badge>
      )}
    </Button>
  )

  if (!notificationsPanel) return bell

  const panelNode = React.isValidElement(notificationsPanel)
    ? React.cloneElement(notificationsPanel as React.ReactElement<{ onNavigate?: () => void }>, {
      onNavigate: () => setOpen(false),
    })
    : notificationsPanel

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{bell}</PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        {panelNode}
      </PopoverContent>
    </Popover>
  )
}
